'use client';

import type { ReactNode } from 'react';
import type { WalletDto } from '@/queries/wallet/wallet.schema';
import { useEffect } from 'react';
import { Icon } from '@/components/entities/Sprite';
import BalanceCard from '@/components/features/layout/Header/components/BalanceCard';
import AnimatedBalance from '@/components/shared/AnimatedBalance';
import PopoverMenu from '@/components/ui/PopoverMenu/PopoverMenu';
import { useCurrency } from '@/hooks/useCurrency';
import { consumeWeb2AppGcPending } from '@/libs/userSource/web2app-campaign';
import AnimatedCurrencyCoin from './AnimatedCurrencyCoin';

interface Props {
  isBalancesOpen: boolean;
  setIsBalancesOpen: (isOpen: boolean) => void;
  coins: WalletDto | undefined;
  balancesPopoverClose: () => void;
}

export default function BalancesPopover({ isBalancesOpen, setIsBalancesOpen, coins, balancesPopoverClose }: Props) {
  const { currency, setCurrency } = useCurrency();

  useEffect(() => {
    if (consumeWeb2AppGcPending()) {
      setCurrency('GC');
    }
  }, [setCurrency]);

  const handleClickCurrency = (isShuffling?: boolean) => {
    if (typeof isShuffling === 'boolean' && isShuffling) {
      return;
    }
    setCurrency(currency === 'GC' ? 'SC' : 'GC');
  };

  return (
    <PopoverMenu
      isOpen={isBalancesOpen}
      onOpenChange={setIsBalancesOpen}
      contentClassName="relative overflow-hidden max-w-[20.5rem] w-full lg:min-w-[18.125rem] bg-c900 ml-[3%] lg:ml-[4%] max-lg:mt-[4%]"
      renderAnchor={(triggerButton: ReactNode) => (
        <div className="bg-c900 flex items-center overflow-hidden rounded-full pl-1.5 py-1.5 text-white paragraph-s-medium">
          <div id="header-balance-target">
            <AnimatedCurrencyCoin
              currency={currency}
              onClick={handleClickCurrency}
            />
          </div>
          {triggerButton}
        </div>
      )}
      trigger={(
        <button
          type="button"
          className="flex items-center gap-2.5 mr-1 ml-2.5 pr-[2.625rem] lg:pr-10 cursor-pointer"
        >
          <div id="header-balance-value-target">
            <AnimatedBalance key={currency} type={currency} />
          </div>
          <Icon name="arrow-down" className="size-4" />
        </button>
      )}
    >
      <BalanceCard coins={coins} balancesPopoverClose={balancesPopoverClose} />
    </PopoverMenu>
  );
}
